import React from "react";
import { shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";
import { CircleMarker, Popup } from "react-leaflet";
import styled from "styled-components";

import { useAppSelector } from "../../redux/hooks";
import variableColors from "../../_App.module.scss";
import useAxios from "../../hooks/useAxios";
import { averageHexColor, getWheelColors } from "../../utils/helperWheelColors";
import { AllSelectedRoutesPolylines, InfoPopup } from "../helpers/PolylinesComponents";
import { StopAvailableRoutes } from "../helpers/Helpers";

const { darkColor } = variableColors;

const InfoPopup2 = styled(InfoPopup)`
  p {
    span {
      text-align: right;
    }
  }
`;

const RoutesInfoMap = ({ agencyId }: { agencyId: string }): JSX.Element => {
  const { t } = useTranslation();

  //* Custom Hook
  const { allRoutes } = useAxios(agencyId);
  // allRoutes?.length && console.log("allRoutes:", allRoutes);

  const [routesDetails, allStopsArrayFiltered, selectedRoutes, selectedRoutesForStops, blackRoutes]: [
    RouteDetails[],
    StopInfo[],
    string[],
    string[],
    boolean
  ] = useAppSelector(
    (state: RootState) => [
      state?.routesInfo?.routesInfo,
      state?.routesInfo?.allStopsArrayFiltered,
      state?.routesList?.selectedRoutes,
      state?.routesList?.selectedRoutesForStops,
      state?.constantValues?.blackRoutes,
    ],
    shallowEqual
  );

  const [linePolylines, setLinePolylines] = React.useState<RouteObj[]>([]);
  const [polylinesStops, setPolylinesStops] = React.useState<StopInfo[]>([]);
  // console.log("linePolylines:", linePolylines);
  // console.log("polylinesStops:", polylinesStops);

  //* Filter selected routes to show
  React.useEffect(() => {
    if (selectedRoutes && allRoutes) {
      const routesToShow: RouteObj[] = allRoutes?.filter((route: RouteObj) => selectedRoutes?.includes(route?.id));
      setLinePolylines(routesToShow);
    }
  }, [allRoutes, selectedRoutes]);

  //* Filter selected stops to show
  React.useEffect(() => {
    if (allStopsArrayFiltered && selectedRoutesForStops && routesDetails) {
      const routesDetailsSelected = routesDetails?.filter((routeDetail: RouteDetails) =>
        selectedRoutesForStops?.includes(routeDetail?.id)
      );

      const stopsIdsToShowFiltered: string[] = [
        ...new Set(routesDetailsSelected?.map((elem: RouteDetails) => elem?.routeStopsIds)?.flat(1)),
      ];

      const stopsToShow: StopInfo[] = allStopsArrayFiltered?.filter((stop: StopInfo) =>
        stopsIdsToShowFiltered?.includes(stop?.stopId)
      );
      setPolylinesStops(stopsToShow);
    }
  }, [allStopsArrayFiltered, routesDetails, selectedRoutesForStops]);

  //* Selected Stops
  const LineStops = (): JSX.Element => {
    // Average color for stops based route colors
    const routesColors: string[] = routesDetails
      // ?.filter((route: RouteDetails) => selectedRoutesForStops?.includes(route?.id)) //* All routes colors vs selected routes colors
      ?.map((filteredRoute: RouteDetails) => filteredRoute?.color);
    // console.log("routesColors:", routesColors);
    const averageColor = averageHexColor(routesColors);
    // console.log("averageColor:", averageColor);

    return (
      <React.Fragment>
        {
          polylinesStops?.map((elem: StopInfo, index: number) => (
            <CircleMarker
              pathOptions={{
                fillColor: blackRoutes ? darkColor : getWheelColors(averageColor, 2)[0],
                color: blackRoutes ? darkColor : getWheelColors(averageColor, 2)[1],
                fillOpacity: 0.8,
                weight: 3,
              }}
              key={String(elem.lat + " " + elem.lon + " " + index)}
              center={{
                lat: elem?.lat as number,
                lng: elem?.lon as number,
              }}
              radius={6}
            >
              <Popup
                autoClose={false}
                closeOnClick={false}
                closeButton={true}
                closeOnEscapeKey={true}
                interactive={true}
                className="stop-popup"
              >
                <InfoPopup2 $width="auto">
                  <p style={{ color: darkColor }}>
                    {t("Nazwa")} <span className="span_bold">{elem?.stopName}</span>
                  </p>
                  <p style={{ color: darkColor }}>
                    {t("Id")} <span className="span_bold">{elem?.stopId}</span>
                  </p>
                  <p className="text-muted small">
                    {t("Pozycja")}: <span className="span_bold text-muted">{`${elem?.lat} ${elem?.lon}`}</span>
                  </p>
                  <StopAvailableRoutes stopId={elem?.stopId} />
                </InfoPopup2>
              </Popup>
            </CircleMarker>
          )) as JSX.Element[]
        }
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {linePolylines?.length ? (
        <AllSelectedRoutesPolylines selectedRoutes={linePolylines} showPopup={true} blackRoutes={blackRoutes} />
      ) : null}
      {polylinesStops && polylinesStops?.length ? LineStops() : null}
    </React.Fragment>
  );
};

export default RoutesInfoMap;
